import { useMemo } from "react";
import { Recommendation } from "@b2bportal/air-shopping-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import { decodeCityName } from "@overrides/utilities";
import dayjs from "dayjs";
import type { WatchCardComponentProps } from "./WatchCard";

const DATE_FORMAT = DateTimeFormatStyle.ShortMonthDayShortWeekday;

export const useWatchCard = (props: WatchCardComponentProps) => {
  const { watch, tripContext } = props;
  const { t, formatDateTime } = useI18nContext();
  const { origin, destination, departureDate, returnDate } = watch.key.value;

  return useMemo(() => {
    const originLabel = decodeCityName(
      tripContext.airports[origin.code]?.servedCityName,
      tripContext.cities[origin.code]?.name,
      origin.code
    );
    const destinationLabel = decodeCityName(
      tripContext.airports[destination.code]?.servedCityName,
      tripContext.cities[destination.code]?.name,
      destination.code
    );

    const title = t("originToDestination", {
      origin: `${originLabel} (${origin.code})`,
      destination: `${destinationLabel} (${destination.code})`,
      // City names are from trusted backend API and have been HTML-decoded
      interpolation: { escapeValue: false },
    });

    return {
      text: {
        title,
        departureDate: formatDateTime(
          dayjs(departureDate).toDate(),
          DATE_FORMAT
        ),
        returnDate:
          returnDate !== null && returnDate !== undefined
            ? formatDateTime(dayjs(returnDate).toDate(), DATE_FORMAT)
            : "",
        tripType:
          returnDate !== null && returnDate !== undefined
            ? t("flightRoundTrip")
            : t("flightOneWay"),
        recommendation:
          watch.recommendation === Recommendation.Buy
            ? t("watch.bookNowTagLabel")
            : t("watch.waitTagLabel"),
      },
    };
  }, [
    departureDate,
    destination.code,
    formatDateTime,
    origin.code,
    returnDate,
    t,
    tripContext.airports,
    tripContext.cities,
    watch.recommendation,
  ]);
};
